import React from 'react';
import { Link } from 'react-router-dom';
import type { UseQueryResult } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { focusSection } from '@/hooks/useScrollToHash';
import type { BudgetSummary, Category } from '@/lib/api';
import { DASHBOARD_SECTIONS, ROUTES } from '@/lib/constants';
import { currentPeriod, formatMonthName, samePeriod, type Period } from '@/lib/format';
import {
  BUDGET_VERDICT_CLASSIFY,
  BUDGET_VERDICT_FIRST_RUN,
  BUDGET_VIEW_BUDGET,
  budgetPastLimit,
  budgetVerdictNoLimit,
  budgetVerdictUsed,
} from '@/lib/translations';
import { budgetVerdict } from '@/lib/utils/budget';

export interface BudgetVerdictProps {
  /** The month the budget reads (the applied period). */
  period: Period;
  summaryQuery: UseQueryResult<BudgetSummary>;
  categoriesQuery: UseQueryResult<Category[]>;
}

// Below sm the link's box grows to 44px tall without moving the line: the padding is taken back by a negative margin.
const linkClass =
  '-my-3 py-3 sm:my-0 sm:py-0 inline-flex items-center gap-1 rounded-sm font-semibold text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

// Painel's answer to "onde você está agora", said once under the period bar before any chart (DESIGN.md, Budget
// Verdict). The Orçamento section below holds the detail; on a phone it starts screens away, so the sentence is
// what most visits read. Neutral like the section: the only colour is the expense dot when a group is past its
// limit. It renders nothing when the budget can't answer (a failed summary — the section says why).
export const BudgetVerdict: React.FC<BudgetVerdictProps> = ({ period, summaryQuery, categoriesQuery }) => {
  const categories = categoriesQuery.data;
  const verdict = categories ? budgetVerdict(summaryQuery.data, categories) : null;
  const pending = categoriesQuery.isPending || (verdict === null && summaryQuery.isPending);

  // Same height as the line, so the sections below don't jump when it lands.
  if (pending) {
    return (
      <div className="flex h-5 items-center" aria-hidden="true">
        <div className="h-3 w-64 max-w-full animate-pulse rounded bg-muted" />
      </div>
    );
  }
  if (!verdict) return null;

  if (verdict.kind === 'first-run') {
    return (
      <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm text-foreground">
        <span>{BUDGET_VERDICT_FIRST_RUN}</span>
        <Link to={ROUTES.categoriesPending} className={linkClass}>
          {BUDGET_VERDICT_CLASSIFY}
          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </Link>
      </p>
    );
  }

  const month = formatMonthName(period);
  // The current month is still running: "dia 18 de 30" says how much of it has passed, so 52% reads as on pace or not.
  const now = new Date();
  const day = samePeriod(period, currentPeriod(now))
    ? { day: now.getDate(), of: new Date(period.year, period.month + 1, 0).getDate() }
    : undefined;

  return (
    <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm tabular-nums text-foreground">
      <span>
        {verdict.kind === 'reading' ? budgetVerdictUsed(month, verdict.overallUsed, day) : budgetVerdictNoLimit(month)}
        {verdict.kind === 'reading' && (
          <>
            <span className="mx-2 text-muted-foreground" aria-hidden="true">
              ·
            </span>
            {verdict.pastLimit > 0 && (
              <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-expense align-middle" aria-hidden="true" />
            )}
            <span className={verdict.pastLimit > 0 ? 'font-semibold' : 'text-muted-foreground'}>
              {budgetPastLimit(verdict.pastLimit)}
            </span>
          </>
        )}
      </span>
      <a
        href={`#${DASHBOARD_SECTIONS.budget}`}
        className={linkClass}
        onClick={(event) => {
          event.preventDefault();
          focusSection(DASHBOARD_SECTIONS.budget);
        }}
      >
        {BUDGET_VIEW_BUDGET}
        <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
      </a>
    </p>
  );
};
